// Cart Context
// Provides cart state and operations to the application

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartApi } from '@/services/cartApi';
import type { Cart, CartItem } from '@/types';

interface CartContextType {
  items: CartItem[];
  itemCount: number;
  total: number;
  isLoading: boolean;
  error: string | null;

  // Operations
  addToCart: (productId: string, variantId?: string, variantAttributes?: Record<string, string>, quantity?: number) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
  syncCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CartProviderProps {
  children: React.ReactNode;
}

export function CartProvider({ children }: CartProviderProps) {
  const [cart, setCart] = useState<Cart | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Derived state
  const items = cart?.items || [];
  const itemCount = cart?.itemCount || 0;
  const total = cart?.total || 0;

  /**
   * Refresh cart from server
   */
  const refreshCart = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await cartApi.getCart();
      setCart(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load cart');
      console.error('Failed to load cart:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Add item to cart
   */
  const addToCart = useCallback(async (
    productId: string,
    variantId?: string,
    variantAttributes?: Record<string, string>,
    quantity = 1
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await cartApi.addToCart({
        productId,
        variantId,
        variantAttributes,
        quantity,
      });

      setCart(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to add item to cart');
      console.error('Failed to add item to cart:', err);
      throw err; // Re-throw for caller to handle
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Update item quantity
   */
  const updateQuantity = useCallback(async (itemId: string, quantity: number) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await cartApi.updateItemQuantity(itemId, quantity);
      setCart(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to update item quantity');
      console.error('Failed to update item quantity:', err);
      throw err; // Re-throw for caller to handle
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Remove item from cart
   */
  const removeItem = useCallback(async (itemId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await cartApi.removeItem(itemId);
      setCart(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to remove item from cart');
      console.error('Failed to remove item from cart:', err);
      throw err; // Re-throw for caller to handle
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Clear entire cart
   */
  const clearCart = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await cartApi.clearCart();
      setCart(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to clear cart');
      console.error('Failed to clear cart:', err);
      throw err; // Re-throw for caller to handle
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Sync cart (recalculate prices and check stock)
   */
  const syncCart = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await cartApi.syncCart();
      setCart(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to sync cart');
      console.error('Failed to sync cart:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load cart on mount (only if user is authenticated)
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      refreshCart();
    }
  }, [refreshCart]);

  const contextValue: CartContextType = {
    items,
    itemCount,
    total,
    isLoading,
    error,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
    refreshCart,
    syncCart,
  };

  return <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>;
}

/**
 * Hook to use cart context
 * Throws error if used outside CartProvider
 */
export function useCart(): CartContextType {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }

  return context;
}
